import type { ComponentProps } from 'react';
import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';

import type Filters from '~/domains/common/components/Filters/Filters';
import { FEATURE } from '~/common/enums/feature.enum';
import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';

import { ContestStatusFilter } from '../../services/enums/contest-status-filter.enum';

import useFieldDescriptors from './useEntriesFilterDescriptors';


function useEntriesFiltersProps({
  status,
}: {
  status: ContestStatusFilter;
}): ComponentProps<typeof Filters> {
  const router = useRouter();
  const { query, replace } = router;
  const isEntriesEntryFeeFilterEnabled = useIsFeatureEnabled(
    FEATURE.ENABLE_ENTRIES_ENTRY_FEE_FILTER
  );

  const FilterDescriptors = useFieldDescriptors();
  const filtersDescriptorsForStatus = useMemo(() => {
    switch (status) {
      case ContestStatusFilter.FINALIZED:
        return [
          FilterDescriptors.SORT_BY_FINALIZED,
          FilterDescriptors.LEAGUES,
          FilterDescriptors.GAME_TYPE,
          isEntriesEntryFeeFilterEnabled ? FilterDescriptors.HAS_ENTRY_FEE : null,
        ].filter(Boolean);
      case ContestStatusFilter.ACTIVE:
      case ContestStatusFilter.ACTION_REQUIRED:
      case ContestStatusFilter.IN_PROGRESS:
      default:
        return [
          FilterDescriptors.SORT_BY_ACTIVE,
          FilterDescriptors.LEAGUES,
          FilterDescriptors.GAME_TYPE,
          FilterDescriptors.STATUS,
          isEntriesEntryFeeFilterEnabled ? FilterDescriptors.HAS_ENTRY_FEE : null,
        ].filter(Boolean);
    }
  }, [
    FilterDescriptors.LEAGUES,
    FilterDescriptors.GAME_TYPE,
    FilterDescriptors.HAS_ENTRY_FEE,
    FilterDescriptors.SORT_BY_ACTIVE,
    FilterDescriptors.SORT_BY_FINALIZED,
    FilterDescriptors.STATUS,
    isEntriesEntryFeeFilterEnabled,
    status,
  ]);

  const values = useMemo(() => {
    if (!filtersDescriptorsForStatus) {
      return {};
    }

    return filtersDescriptorsForStatus.reduce((acc, filter) => {
      const value = filter.fromQuery(query);
      if (value) {
        acc[filter.object.key] = value;
      }

      return acc;
    }, {});
  }, [filtersDescriptorsForStatus, query]);

  const filters = useMemo(
    () =>
      filtersDescriptorsForStatus.map((filter) => {
        const activeKeyValue = values[filter.object.key];

        return {
          ...filter.object,
          isActive: activeKeyValue !== undefined,
          activeLabel: activeKeyValue
            ? filter.object.options.find((option) => option.value === activeKeyValue?.[0])?.label
            : undefined,
        };
      }),
    [filtersDescriptorsForStatus, values]
  );

  const handleChange = useCallback(
    (newValues: ComponentProps<typeof Filters>['values']) => {
      let newQuery = Object.fromEntries<string | string[]>(
        new URLSearchParams(window.location.search)
      );

      Object.entries(newValues).forEach(([key, value]) => {
        const filter = filtersDescriptorsForStatus.find((f) => f.object.key === key);
        if (!filter) {
          return;
        }

        const queryValue = filter.toQuery(value);
        newQuery = { ...newQuery, ...queryValue };
      });

      const filteredQuery = Object.fromEntries(
        Object.entries(newQuery).filter(([_, value]) => value !== null)
      );
      replace({ query: filteredQuery }, undefined, { shallow: true });
    },
    [filtersDescriptorsForStatus, replace]
  );

  return {
    filters,
    values,
    onChange: handleChange,
  };
}

export default useEntriesFiltersProps;
